import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import FilePicker from "../components/FilePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function NaturalCommand() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Auto-select file + prefill instruction from URL params (e.g. from Dashboard examples)
  useEffect(() => {
    const fid = searchParams.get("file_id");
    const q = searchParams.get("q");
    if (q) setInstruction(q);
    if (fid && !file) {
      api.get("/files").then(({ data }) => {
        const found = data.find((f) => f.id === fid);
        if (found) setFile(found);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function generatePlan() {
    if (!file || !instruction.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/plan", {
        file_id: file.id,
        instruction,
      });
      setPlan(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  async function runPlan() {
    if (!plan?.steps?.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pipeline/run", {
        file_id: file.id,
        steps: plan.steps,
      });
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12" data-testid="natural-command-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_natural_command")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_natural_command_desc")}</p>

      <div className="mt-8 grid gap-6">
        {!file ? (
          <FilePicker accept="" onSelected={(f) => setFile(f)} />
        ) : (
          <>
            <div className="border border-zinc-200 rounded-md p-6 bg-white">
              <span className="eyebrow">FILE</span>
              <div className="hero-h2 text-xl mt-1">{file.original_filename}</div>
              <Button variant="ghost" className="rounded-sm mt-3" onClick={() => { setFile(null); setPlan(null); setResult(null); }}
                data-testid="nc-clear">
                {t("cta_clear")}
              </Button>
            </div>

            <div>
              <Label className="eyebrow">{t("nc_label")}</Label>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder={t("nc_placeholder")}
                className="w-full mt-2 rounded-sm border border-zinc-300 p-3 min-h-[120px] focus-visible:ring-1 focus-visible:ring-black focus-visible:outline-none font-light"
                data-testid="nc-instruction"
              />
              <Button onClick={generatePlan} disabled={busy || !instruction.trim()}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11 mt-3"
                data-testid="nc-generate">
                {busy ? t("processing") : `${t("cta_continue")} →`}
              </Button>
            </div>

            {plan && (
              <div className="border border-zinc-300 rounded-md p-6 fade-up" data-testid="nc-plan">
                <span className="eyebrow">{t("nc_plan")}</span>
                {plan.explanation && <p className="text-zinc-600 mt-2 font-light">{plan.explanation}</p>}
                <ol className="mt-4 grid gap-2">
                  {plan.steps.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 border-l-2 border-zinc-200 pl-3" data-testid={`nc-step-${i}`}>
                      <span className="eyebrow w-6">{i + 1}</span>
                      <pre className="mono text-xs whitespace-pre-wrap">{JSON.stringify(s)}</pre>
                    </li>
                  ))}
                </ol>
                {plan.steps.length > 0 && (
                  <Button onClick={runPlan} disabled={busy}
                    className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11 mt-5"
                    data-testid="nc-run">
                    {busy ? t("processing") : `${t("nc_run")} →`}
                  </Button>
                )}
              </div>
            )}

            {result && (
              <div className="border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="nc-result">
                <span className="eyebrow">{t("result_ready")}</span>
                <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
                {result.log && (
                  <ul className="mt-3 grid gap-1 mono text-xs text-zinc-500">
                    {result.log.map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                )}
                <Button onClick={() => downloadFile(result.id, result.original_filename)}
                  className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
                  ↓ {t("cta_download")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
